import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Phone, ShieldCheck, Clock, MapPin, KeyRound, DoorOpen, Lock, Wrench, BadgeCheck, Star, ArrowUpRight, Loader2, Check, Mail } from "lucide-react";
import FrenchFlag from "@/components/FrenchFlag";
import CityMarquee from "@/components/CityMarquee";
import FloatingCallButton from "@/components/FloatingCallButton";
import SeoZonesFooter from "@/components/SeoZonesFooter";
import AprilBanner from "@/components/AprilBanner";
import CITIES from "@/data/loiret-cities";

const TEL = "06 49 50 18 43";
const TEL_HREF = "tel:+33649501843";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Photos identiques à celles de la page Accueil / site principal
const HERO_IMG = "https://customer-assets.emergentagent.com/job_serrurerie-atelier/artifacts/yo45r6j5_74abc5c1-80d6-4b5a-8f70-1594cbf88ec3.jpeg";
const STORE_IMG = "https://customer-assets.emergentagent.com/job_serrurerie-atelier/artifacts/1oxvfxt2_secretaire%20serrurier.jpeg";
const VAN_IMG = "https://customer-assets.emergentagent.com/job_serrurerie-atelier/artifacts/yxk6l04r_e498bd56-735d-47f8-9526-69da3334e3aa.jpeg";
const BRANDS_IMG = "https://customer-assets.emergentagent.com/job_serrurerie-atelier/artifacts/z5lc5i3l_serrurier%20marques%20serrures.jpeg";
const BADGE_IMG = "https://customer-assets.emergentagent.com/job_serrurerie-atelier/artifacts/kczqfo00_artisan%20serrurier%20beauchamp.jpeg";

const Section = ({ children, className = "" }) => (
    <section className={`max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 ${className}`}>{children}</section>
);

const SerrurierOrleans = () => {
    useEffect(() => {
        // SEO meta tags — title raccourci (60 chars max recommandé par Google)
        document.title = "Serrurier Orléans 24/7 · Atelier Beauchamp · 30 min";
        const setMeta = (name, content) => {
            let el = document.querySelector(`meta[name="${name}"]`);
            if (!el) {
                el = document.createElement("meta");
                el.setAttribute("name", name);
                document.head.appendChild(el);
            }
            el.setAttribute("content", content);
        };
        // Description ~155 chars, accroche action + mot-clé + bénéfice + téléphone
        setMeta("description", "Serrurier Orléans 45000 disponible 24h/24 · Ouverture porte claquée, dépannage urgent, remplacement serrure, porte blindée · Intervention en 30 min · 06 49 50 18 43.");
        setMeta("google-site-verification", "google07d8a4f884c7c348");
        setMeta("keywords", "serrurier Orléans, serrurier 45000, serrurier Loiret, urgence serrurier Orléans, ouverture porte claquée Orléans, dépannage serrurier Orléans 24h, serrurerie Orléans, artisan serrurier Orléans, serrurier pas cher Orléans, remplacement serrure Orléans, porte blindée Orléans, serrurier nuit Orléans, serrurier dimanche Orléans, serrurier pas cher Loiret, serrurier rapide Orléans, dépannage porte Orléans");
        setMeta("robots", "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1");
        setMeta("author", "Atelier Beauchamp");
        setMeta("geo.region", "FR-45");
        setMeta("geo.placename", "Orléans");
        setMeta("geo.position", "47.902964;1.909251");
        setMeta("ICBM", "47.902964, 1.909251");
        // Open Graph / partage social
        const setOg = (prop, content) => {
            let el = document.querySelector(`meta[property="${prop}"]`);
            if (!el) {
                el = document.createElement("meta");
                el.setAttribute("property", prop);
                document.head.appendChild(el);
            }
            el.setAttribute("content", content);
        };
        setOg("og:title", "Serrurier Orléans 24/7 — Atelier Beauchamp · 06 49 50 18 43");
        setOg("og:description", "Serrurier Orléans 45000 · Intervention en 30 min · Ouverture porte claquée, dépannage urgent, remplacement serrure, porte blindée. Artisan certifié, devis gratuit, disponible 24h/24.");
        setOg("og:type", "website");
        setOg("og:locale", "fr_FR");
        setOg("og:site_name", "Atelier Beauchamp");
        setOg("og:url", "https://serrurerie-artisan-beauchamp.fr/serrurier-orleans-45");
        setOg("og:image", "https://serrurerie-artisan-beauchamp.fr/logo512.png");
        setOg("og:image:width", "512");
        setOg("og:image:height", "512");
        // Twitter Card
        setMeta("twitter:card", "summary_large_image");
        setMeta("twitter:title", "Serrurier Orléans 24/7 — Atelier Beauchamp");
        setMeta("twitter:description", "Urgence serrurerie à Orléans · Intervention en 30 min · Artisan certifié · 06 49 50 18 43.");
        setMeta("twitter:image", "https://serrurerie-artisan-beauchamp.fr/logo512.png");
        // Canonical + hreflang
        let canon = document.querySelector('link[rel="canonical"]');
        if (!canon) {
            canon = document.createElement("link");
            canon.setAttribute("rel", "canonical");
            document.head.appendChild(canon);
        }
        canon.setAttribute("href", "https://serrurerie-artisan-beauchamp.fr/serrurier-orleans-45");
        let hreflang = document.querySelector('link[hreflang="fr-FR"]');
        if (!hreflang) {
            hreflang = document.createElement("link");
            hreflang.setAttribute("rel", "alternate");
            hreflang.setAttribute("hreflang", "fr-FR");
            document.head.appendChild(hreflang);
        }
        hreflang.setAttribute("href", "https://serrurerie-artisan-beauchamp.fr/serrurier-orleans-45");
        // ───── Schema.org structured data ─────
        // 1) LocalBusiness (Locksmith) enrichi
        let ld = document.getElementById("ld-serrurier-orleans");
        if (!ld) {
            ld = document.createElement("script");
            ld.type = "application/ld+json";
            ld.id = "ld-serrurier-orleans";
            document.head.appendChild(ld);
        }
        ld.textContent = JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Locksmith",
            "@id": "https://serrurerie-artisan-beauchamp.fr/serrurier-orleans-45#business",
            "name": "Atelier Beauchamp — Serrurier Orléans",
            "alternateName": "Serrurier Orléans Atelier Beauchamp",
            "description": "Artisan serrurier 24h/24 à Orléans et dans le Loiret — ouverture de porte claquée, dépannage urgent, remplacement de serrure, pose de porte blindée. Intervention en 30 min, devis gratuit.",
            "image": [
                "https://serrurerie-artisan-beauchamp.fr/logo512.png",
                HERO_IMG,
                VAN_IMG
            ],
            "logo": "https://serrurerie-artisan-beauchamp.fr/logo512.png",
            "url": "https://serrurerie-artisan-beauchamp.fr/serrurier-orleans-45",
            "telephone": "+33649501843",
            "priceRange": "€€",
            "currenciesAccepted": "EUR",
            "paymentAccepted": "Cash, Credit Card, Check, Bank Transfer",
            "address": {
                "@type": "PostalAddress",
                "streetAddress": "Orléans",
                "addressLocality": "Orléans",
                "addressRegion": "Centre-Val de Loire",
                "postalCode": "45000",
                "addressCountry": "FR"
            },
            "geo": {
                "@type": "GeoCoordinates",
                "latitude": 47.902964,
                "longitude": 1.909251
            },
            "areaServed": [
                { "@type": "City", "name": "Orléans", "sameAs": "https://fr.wikipedia.org/wiki/Orl%C3%A9ans" },
                { "@type": "City", "name": "Saint-Jean-de-Braye" },
                { "@type": "City", "name": "Saint-Jean-de-la-Ruelle" },
                { "@type": "City", "name": "Olivet" },
                { "@type": "City", "name": "Fleury-les-Aubrais" },
                { "@type": "City", "name": "Saran" },
                { "@type": "City", "name": "Chécy" },
                { "@type": "City", "name": "Ingré" },
                { "@type": "City", "name": "Pithiviers" },
                { "@type": "City", "name": "Montargis" },
                { "@type": "City", "name": "Gien" },
                { "@type": "AdministrativeArea", "name": "Loiret", "sameAs": "https://fr.wikipedia.org/wiki/Loiret_(d%C3%A9partement)" }
            ],
            "openingHoursSpecification": {
                "@type": "OpeningHoursSpecification",
                "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
                "opens": "00:00",
                "closes": "23:59"
            },
            "hasOfferCatalog": {
                "@type": "OfferCatalog",
                "name": "Services serrurerie Orléans",
                "itemListElement": [
                    { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Ouverture de porte claquée Orléans", "description": "Ouverture non destructive en moins de 30 minutes." } },
                    { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Dépannage serrurier urgent Orléans", "description": "Serrure grippée, clé cassée, mécanisme bloqué — réparation en une visite." } },
                    { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Remplacement de serrure Orléans", "description": "Pose de serrures A2P 1, 2 ou 3 étoiles — Fichet, Bricard, Vachette, Picard." } },
                    { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Pose de porte blindée Orléans", "description": "Porte blindée BP1, BP2 ou BP3 avec serrure multipoints et cylindre haute sécurité." } }
                ]
            },
            "aggregateRating": { "@type": "AggregateRating", "ratingValue": "5", "reviewCount": "127", "bestRating": "5", "worstRating": "1" },
            "review": [
                { "@type": "Review", "author": { "@type": "Person", "name": "Catherine M." }, "reviewRating": { "@type": "Rating", "ratingValue": "5" }, "reviewBody": "Intervention en 20 min un dimanche soir. Porte claquée ouverte sans aucun dégât." },
                { "@type": "Review", "author": { "@type": "Person", "name": "Jean-Marc D." }, "reviewRating": { "@type": "Rating", "ratingValue": "5" }, "reviewBody": "Remplacement de serrure suite à perte de clés. Conseil de qualité, pose impeccable." },
                { "@type": "Review", "author": { "@type": "Person", "name": "Sophie R." }, "reviewRating": { "@type": "Rating", "ratingValue": "5" }, "reviewBody": "Pose d'une porte blindée. Travail soigné, aucune dégradation, très satisfaite." }
            ],
            "sameAs": [
                "https://serrurerie-artisan-beauchamp.fr/"
            ]
        });
        // 2) FAQPage schema — rich snippet possible dans Google
        let faqLd = document.getElementById("ld-faq-orleans");
        if (!faqLd) {
            faqLd = document.createElement("script");
            faqLd.type = "application/ld+json";
            faqLd.id = "ld-faq-orleans";
            document.head.appendChild(faqLd);
        }
        faqLd.textContent = JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
                { "@type": "Question", "name": "Combien de temps pour qu'un serrurier arrive à Orléans ?", "acceptedAnswer": { "@type": "Answer", "text": "Notre délai moyen est de 25 à 30 minutes à Orléans intra-muros, et de 30 à 45 minutes en périphérie du Loiret. En cas d'extrême urgence (personne enfermée, enfant ou animal à l'intérieur, fuite d'eau), nous priorisons systématiquement le déplacement." } },
                { "@type": "Question", "name": "Quel est le tarif d'un serrurier à Orléans en pleine nuit ?", "acceptedAnswer": { "@type": "Answer", "text": "Une intervention de nuit (entre 20h et 7h), un dimanche ou un jour férié applique une majoration légale standard. Le tarif exact vous est annoncé clairement par téléphone avant tout déplacement." } },
                { "@type": "Question", "name": "Le serrurier peut-il ouvrir une porte sans casser ?", "acceptedAnswer": { "@type": "Answer", "text": "Oui, dans 95 % des cas. Nous utilisons des techniques non destructives : crochetage, méthode du carton, ouverture aux radios, outils spécialisés pour les serrures multipoints et portes blindées." } },
                { "@type": "Question", "name": "Êtes-vous agréé par les assurances ?", "acceptedAnswer": { "@type": "Answer", "text": "Oui. Atelier Beauchamp est inscrit à la CMA, dispose d'une assurance RC professionnelle et une décennale April et travaille avec les principales compagnies (Maif, Macif, Matmut, AXA, Generali, Allianz, MMA, Groupama, Crédit Agricole). Nous fournissons tous les justificatifs nécessaires." } },
                { "@type": "Question", "name": "Quelles marques de serrures installez-vous ?", "acceptedAnswer": { "@type": "Answer", "text": "Toutes les grandes marques : Fichet, Bricard, Vachette, Picard, JPM, Mottura, Kaba, Héraclès, Mul-T-Lock, Pollux, Reelax, Tesa, City, Iseo, Dom, FTH Thirard, Dierre, Abus." } },
                { "@type": "Question", "name": "Intervenez-vous le dimanche et les jours fériés ?", "acceptedAnswer": { "@type": "Answer", "text": "Oui, 7 jours sur 7, 24 heures sur 24, y compris dimanches, jours fériés et nuits. Notre service de garde fonctionne en continu pour toutes les urgences sur Orléans et le Loiret." } }
            ]
        });
        // 3) BreadcrumbList schema
        let breadLd = document.getElementById("ld-breadcrumb-orleans");
        if (!breadLd) {
            breadLd = document.createElement("script");
            breadLd.type = "application/ld+json";
            breadLd.id = "ld-breadcrumb-orleans";
            document.head.appendChild(breadLd);
        }
        breadLd.textContent = JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Accueil", "item": "https://serrurerie-artisan-beauchamp.fr/" },
                { "@type": "ListItem", "position": 2, "name": "Serrurier Orléans 45", "item": "https://serrurerie-artisan-beauchamp.fr/serrurier-orleans-45" }
            ]
        });
        return () => { document.title = "Atelier Beauchamp — Serrurier 24/7 · Dépannage Express"; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="min-h-screen bg-[#FAFAFA] text-[#0A0A0A] font-sans" data-testid="serrurier-orleans-page">
            {/* --- Urgency banner --- */}
            <div className="bg-[#E63946] text-white">
                <a href={TEL_HREF} className="flex items-center justify-center gap-3 py-3 px-4 hover:bg-[#D62828] transition-colors">
                    <Phone className="h-4 w-4 animate-pulse" strokeWidth={2.5} />
                    <span className="font-bold uppercase tracking-wide text-xs sm:text-sm">Urgence Serrurier Orléans 24/7 — Appel direct {TEL}</span>
                </a>
            </div>

            {/* --- Sticky brand bar (logo + drapeau reste fixe) --- */}
            <div className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm border-b border-black/10 h-16 lg:h-20 flex items-center">
                <div className="max-w-6xl mx-auto w-full px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-3">
                    <a href="#top" className="flex items-center gap-3">
                        <img src="/logo512.png" alt="Atelier Beauchamp" className="h-10 sm:h-12 w-auto" />
                        <div className="leading-tight">
                            <div className="font-display font-bold text-sm sm:text-base uppercase tracking-tight flex items-center gap-2">
                                Atelier Beauchamp
                                <FrenchFlag />
                            </div>
                            <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-medium">Serrurier Orléans · Loiret 45</div>
                        </div>
                    </a>
                    <a href={TEL_HREF} className="hidden sm:inline-flex items-center gap-2 bg-[#E63946] hover:bg-[#D62828] text-white px-4 py-2.5 transition-colors">
                        <Phone className="h-4 w-4" strokeWidth={2.5} />
                        <span className="font-display font-bold text-sm">{TEL}</span>
                    </a>
                </div>
            </div>

            {/* --- City Marquee (scrolling phone banner, plus rapide + label spécifique) --- */}
            <CityMarquee label="Serrurier Orléans Express" speedSeconds={15} />

            <AprilBanner />

            {/* --- HERO --- */}
            <header id="top" className="bg-white border-b border-black/10">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
                        <div className="lg:col-span-7">
                            <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#E63946] mb-3">◉ Urgence Serrurerie · Loiret 45000</div>
                            <h1 className="font-display font-bold uppercase text-3xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.95]">
                                Serrurier Orléans<br />
                                <span className="text-[#E63946]">24h/24 · 7j/7</span><br />
                                Intervention en 30 min
                            </h1>
                            <p className="mt-6 text-base sm:text-lg text-zinc-700 leading-relaxed">
                                <strong>Atelier Beauchamp</strong>, votre <strong>serrurier à Orléans</strong> (45000) et dans tout le Loiret,
                                intervient en urgence pour <strong>ouverture de porte claquée</strong>, <strong>dépannage serrurier</strong>,
                                <strong> remplacement de serrure</strong> et <strong>pose de porte blindée</strong>.
                                Artisan certifié CMA, disponible jour et nuit, week-ends et jours fériés.
                            </p>

                            <div className="mt-8 flex flex-col sm:flex-row gap-3">
                                <a href={TEL_HREF} className="inline-flex items-center justify-between gap-4 bg-[#E63946] hover:bg-[#D62828] text-white px-6 py-5 transition-colors" data-testid="hero-call-btn">
                                    <span className="flex items-center gap-3">
                                        <Phone className="h-5 w-5" strokeWidth={2.5} />
                                        <span className="font-display font-bold text-xl tracking-tight">{TEL}</span>
                                    </span>
                                    <ArrowUpRight className="h-5 w-5" />
                                </a>
                                <a href="#devis-form" className="inline-flex items-center justify-between gap-4 bg-[#0A0A0A] text-white px-6 py-5 hover:bg-zinc-800 transition-colors" data-testid="hero-quote-btn">
                                    <span className="font-semibold text-sm uppercase tracking-[0.15em]">Devis gratuit</span>
                                    <ArrowUpRight className="h-5 w-5" />
                                </a>
                            </div>
                        </div>

                        <div className="lg:col-span-5">
                            <div className="relative bg-white border border-black/10">
                                <img
                                    src={HERO_IMG}
                                    alt="Artisan serrurier Atelier Beauchamp à Orléans"
                                    className="w-full aspect-square sm:aspect-auto sm:h-[460px] object-cover"
                                    loading="eager"
                                />
                                <div className="absolute top-4 left-4 bg-white border border-black/10 px-3 py-2 flex items-center gap-2">
                                    <span className="relative flex h-2 w-2">
                                        <span className="absolute inline-flex h-full w-full rounded-full bg-[#E63946] opacity-75 pulse-dot"></span>
                                        <span className="relative inline-flex rounded-full h-2 w-2 bg-[#E63946]"></span>
                                    </span>
                                    <span className="text-[11px] uppercase tracking-[0.18em] font-semibold">En ligne · répond maintenant</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-4 border-t border-black/10 pt-6">
                        {[
                            { icon: Clock, t: "Sous 30 min", s: "Délai moyen" },
                            { icon: ShieldCheck, t: "Agréé & diplômé", s: "Artisan certifié" },
                            { icon: BadgeCheck, t: "Devis gratuit", s: "Sans engagement" },
                            { icon: MapPin, t: "Orléans 45", s: "Loiret entier" },
                        ].map((b, i) => (
                            <div key={i} className="flex items-start gap-3">
                                <b.icon className="h-5 w-5 text-[#E63946] mt-0.5" strokeWidth={2.4} />
                                <div>
                                    <div className="font-display font-bold text-sm">{b.t}</div>
                                    <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">{b.s}</div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </header>

            {/* --- Trust badges (CMA / Devis Gratuit / Artisan Beauchamp) --- */}
            <section className="bg-white border-b border-black/10">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
                    <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-zinc-500 text-center mb-6">◉ Certifications & engagements</div>
                    <div className="grid grid-cols-3 gap-4 sm:gap-8 items-end">
                        <div className="flex flex-col items-center">
                            <img src="https://customer-assets.emergentagent.com/job_serrurerie-atelier/artifacts/x9to35vk_artisan%20serrurier.jpg" alt="Inscrit à la Chambre de Métiers et de l'Artisanat" className="w-full max-h-28 object-contain" />
                            <div className="mt-3 text-[10px] sm:text-xs uppercase tracking-[0.18em] font-bold text-zinc-800 text-center">Inscrit CMA</div>
                        </div>
                        <div className="flex flex-col items-center">
                            <img src="https://customer-assets.emergentagent.com/job_serrurerie-atelier/artifacts/t9krimpt_devis-gratuit.png" alt="Devis gratuit serrurier Orléans" className="w-full max-h-28 object-contain" />
                            <div className="mt-3 text-[10px] sm:text-xs uppercase tracking-[0.18em] font-bold text-zinc-800 text-center">Devis gratuit</div>
                        </div>
                        <div className="flex flex-col items-center">
                            <img src={BADGE_IMG} alt="Artisan Serrurier Beauchamp certifié" className="w-full max-h-28 object-contain" />
                            <div className="mt-3 text-[10px] sm:text-xs uppercase tracking-[0.18em] font-bold text-zinc-800 text-center">Artisan certifié</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* --- 1. Pourquoi un serrurier Orléans 24h/24 --- */}
            <Section>
                <h2 className="font-display font-bold uppercase text-2xl sm:text-3xl tracking-tight leading-tight mb-5">
                    Pourquoi choisir un serrurier à Orléans disponible 24h/24 ?
                </h2>
                <p className="text-base text-zinc-700 leading-relaxed mb-4">
                    Une <strong>porte claquée à 22h</strong>, une <strong>clé cassée dans la serrure</strong> un dimanche matin, un <strong>cambriolage</strong> qui laisse votre logement sans sécurité…
                    Les urgences serrurerie n'attendent jamais les heures ouvrables. C'est pourquoi <strong>Atelier Beauchamp</strong>, votre <strong>serrurier de proximité à Orléans</strong>,
                    s'engage à intervenir <strong>24 heures sur 24 et 7 jours sur 7</strong>, y compris la nuit, le week-end et les jours fériés.
                </p>
                <p className="text-base text-zinc-700 leading-relaxed mb-4">
                    Basé à <strong>Orléans (45000)</strong>, notre artisan serrurier indépendant couvre l'ensemble du <strong>département du Loiret</strong> :
                    Orléans centre, La Source, Saint-Marceau, Saint-Jean-de-Braye, Saint-Jean-de-la-Ruelle, Fleury-les-Aubrais, Olivet, Saran, Chécy, Ormes,
                    Ingré, Saint-Cyr-en-Val, Marigny-les-Usages, ainsi que Pithiviers, Montargis, Gien, Beaugency, Châteauneuf-sur-Loire et tout le bassin orléanais.
                </p>
                <p className="text-base text-zinc-700 leading-relaxed">
                    En cas d'urgence, composez le <a href={TEL_HREF} className="text-[#E63946] font-semibold hover:underline">{TEL}</a>.
                    Vous tombez directement sur un <strong>artisan diplômé</strong>, pas sur une plateforme téléphonique. Le tarif vous est <strong>annoncé clairement au téléphone</strong>,
                    avant même le déplacement — pas de surprise, pas de frais cachés.
                </p>
            </Section>

            {/* --- 2. Nos prestations --- */}
            <Section className="bg-white border-y border-black/10">
                <h2 className="font-display font-bold uppercase text-2xl sm:text-3xl tracking-tight leading-tight mb-6">
                    Nos prestations de serrurerie à Orléans
                </h2>

                <div className="space-y-8">
                    <article>
                        <div className="flex items-center gap-3 mb-3">
                            <DoorOpen className="h-6 w-6 text-[#E63946]" strokeWidth={2.4} />
                            <h3 className="font-display font-bold uppercase text-lg tracking-tight">Ouverture de porte claquée à Orléans</h3>
                        </div>
                        <p className="text-base text-zinc-700 leading-relaxed">
                            Vous êtes resté à l'extérieur de chez vous ? Votre <strong>porte est claquée sans la clé</strong> ?
                            Notre serrurier intervient en <strong>moins de 30 minutes</strong> à Orléans et dans le Loiret pour une <strong>ouverture de porte non destructive</strong>.
                            Grâce à des techniques professionnelles (méthode dite « du carton » pour les portes claquées simples,
                            crochetage fin pour les serrures à goupilles, ouverture aux radios pour les serrures classiques), nous ouvrons votre porte <strong>sans aucun dégât</strong> dans 95 % des cas.
                            Pour les <strong>portes blindées</strong>, <strong>serrures multipoints</strong> ou <strong>serrures A2P 3 étoiles</strong>, des outils adaptés permettent une intervention propre et réversible.
                        </p>
                    </article>

                    <article>
                        <div className="flex items-center gap-3 mb-3">
                            <Wrench className="h-6 w-6 text-[#E63946]" strokeWidth={2.4} />
                            <h3 className="font-display font-bold uppercase text-lg tracking-tight">Dépannage serrurier urgent Orléans</h3>
                        </div>
                        <p className="text-base text-zinc-700 leading-relaxed">
                            <strong>Serrure grippée</strong>, <strong>clé cassée dans le barillet</strong>, <strong>poignée arrachée</strong>, <strong>verrou bloqué</strong>,
                            <strong> mécanisme bloqué après tentative d'effraction</strong> — Atelier Beauchamp réalise tout type de <strong>dépannage serrurerie en urgence à Orléans</strong>.
                            Notre véhicule d'intervention est équipé en pièces détachées (cylindres européens, barillets haute sécurité, gâches, têtières, pênes)
                            pour résoudre la majorité des pannes <strong>en une seule intervention</strong>, sans rendez-vous différé. Après un cambriolage, nous mettons votre porte
                            <strong> en sécurité immédiate</strong>, expertisons les dégâts pour votre dépôt de plainte, et vous fournissons <strong>tous les justificatifs nécessaires</strong> pour votre
                            <strong> assurance habitation</strong>.
                        </p>
                    </article>

                    <article>
                        <div className="flex items-center gap-3 mb-3">
                            <KeyRound className="h-6 w-6 text-[#E63946]" strokeWidth={2.4} />
                            <h3 className="font-display font-bold uppercase text-lg tracking-tight">Remplacement de serrure Orléans</h3>
                        </div>
                        <p className="text-base text-zinc-700 leading-relaxed">
                            Vous emménagez dans un nouveau logement à Orléans ? Vous avez perdu vos clés ? Vous vous séparez d'un colocataire ?
                            Le <strong>remplacement de serrure</strong> est la solution la plus sûre pour reprendre le contrôle de votre sécurité.
                            Atelier Beauchamp pose toutes les <strong>grandes marques de serrurerie</strong> : <strong>Fichet</strong>, <strong>Bricard</strong>, <strong>Vachette</strong>,
                            <strong> Picard</strong>, <strong>JPM</strong>, <strong>Mottura</strong>, <strong>Kaba</strong>, <strong>Héraclès</strong>, <strong>Mul-T-Lock</strong>, <strong>Pollux</strong>,
                            <strong> Reelax</strong>, <strong>Tesa</strong>, <strong>City</strong>. Nous installons des serrures certifiées <strong>A2P (Assurance Prévention Protection)</strong>
                            niveau 1, 2 ou 3 étoiles selon votre niveau d'exigence et les recommandations de votre assurance.
                        </p>
                    </article>

                    <article>
                        <div className="flex items-center gap-3 mb-3">
                            <Lock className="h-6 w-6 text-[#E63946]" strokeWidth={2.4} />
                            <h3 className="font-display font-bold uppercase text-lg tracking-tight">Pose de porte blindée Orléans</h3>
                        </div>
                        <p className="text-base text-zinc-700 leading-relaxed">
                            Pour la protection définitive de votre habitation, nous installons des <strong>portes blindées</strong> certifiées <strong>BP 1, BP 2 ou BP 3</strong>,
                            adaptées aussi bien aux appartements qu'aux maisons individuelles. Une vraie porte blindée se compose d'un <strong>bloc-porte en acier</strong>,
                            d'une <strong>serrure multipoints 3, 5 ou 7 points</strong>, d'un <strong>cylindre haute sécurité anti-perçage et anti-crochetage</strong>,
                            d'un <strong>dormant renforcé</strong>, de <strong>paumelles anti-dégondage</strong> et d'une <strong>protection anti-pince-monseigneur</strong>.
                            La pose dure entre 4 et 8 heures selon la configuration et n'occasionne <strong>aucune dégradation du sol ni du chambranle</strong>.
                        </p>
                    </article>
                </div>

                {/* Photos illustration — porte blindée + serrure */}
                <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <figure className="relative bg-white border border-black/10 overflow-hidden">
                        <img
                            src="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=900&q=80"
                            alt="Porte blindée installée par Atelier Beauchamp à Orléans"
                            className="w-full h-[280px] sm:h-[340px] object-cover"
                            loading="lazy"
                        />
                        <figcaption className="absolute bottom-0 left-0 right-0 bg-white/95 px-4 py-3 border-t border-black/10">
                            <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#E63946]">Porte blindée</div>
                            <div className="text-sm font-display font-bold">Certifiée BP1 / BP2 / BP3 — pose sans dégradation</div>
                        </figcaption>
                    </figure>
                    <figure className="relative bg-white border border-black/10 overflow-hidden">
                        <img
                            src="https://images.unsplash.com/photo-1582139329536-e7284fece509?auto=format&fit=crop&w=900&q=80"
                            alt="Serrure haute sécurité A2P remplacement Orléans"
                            className="w-full h-[280px] sm:h-[340px] object-cover"
                            loading="lazy"
                        />
                        <figcaption className="absolute bottom-0 left-0 right-0 bg-white/95 px-4 py-3 border-t border-black/10">
                            <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#E63946]">Serrure haute sécurité</div>
                            <div className="text-sm font-display font-bold">A2P 1, 2 ou 3 étoiles — anti-perçage et anti-crochetage</div>
                        </figcaption>
                    </figure>
                </div>
            </Section>
            <Section>
                <h2 className="font-display font-bold uppercase text-2xl sm:text-3xl tracking-tight leading-tight mb-5">
                    Tarifs serrurier Orléans : transparence et devis gratuit
                </h2>
                <p className="text-base text-zinc-700 leading-relaxed mb-4">
                    Combien coûte un <strong>serrurier à Orléans</strong> ? Le prix dépend de la nature de l'intervention, du type de serrure et de l'horaire (jour, nuit, week-end, jour férié).
                    Chez Atelier Beauchamp, nous appliquons une politique tarifaire transparente : <strong>le tarif vous est annoncé au téléphone</strong>, avant le déplacement.
                    Si vous l'acceptez, il devient le <strong>prix final ferme</strong> de l'intervention — sans rallonge, sans surfacturation surprise une fois sur place.
                </p>
                <p className="text-base text-zinc-700 leading-relaxed mb-4">
                    Pour vous donner un ordre d'idée : une <strong>ouverture de porte claquée simple en journée</strong> commence aux alentours de 80 € à 150 € selon la complexité.
                    Une <strong>intervention de nuit ou un dimanche</strong> applique une majoration légale standard. Un <strong>remplacement de serrure standard</strong>
                    avec pièces se situe généralement entre 150 € et 350 €. La pose d'une <strong>porte blindée d'entrée</strong> démarre à partir d'environ 1 500 € fournie posée.
                    Tous les <strong>devis sont gratuits et sans engagement</strong>, qu'ils soient demandés par téléphone ou via le formulaire en bas de cette page.
                </p>
                <p className="text-base text-zinc-700 leading-relaxed">
                    Atelier Beauchamp accepte les paiements par <strong>carte bancaire</strong>, <strong>espèces</strong>, <strong>chèque</strong> et <strong>virement</strong>.
                    Une <strong>facture détaillée</strong> vous est systématiquement remise après chaque intervention — utile pour votre suivi personnel et obligatoire pour votre assurance.
                </p>
            </Section>

            {/* --- 4. Zone d'intervention --- */}
            <Section className="bg-white border-y border-black/10">
                <h2 className="font-display font-bold uppercase text-2xl sm:text-3xl tracking-tight leading-tight mb-5">
                    Zone d'intervention : Orléans et tout le Loiret
                </h2>
                <p className="text-base text-zinc-700 leading-relaxed mb-4">
                    Notre <strong>serrurier intervient à Orléans</strong> et dans toutes les communes du Loiret (45). Voici une liste non exhaustive des secteurs couverts :
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm text-zinc-700 mb-5">
                    {[
                        "Orléans 45000", "Orléans La Source", "Saint-Jean-de-Braye 45800", "Saint-Jean-de-la-Ruelle 45140",
                        "Fleury-les-Aubrais 45400", "Olivet 45160", "Saran 45770", "Chécy 45430",
                        "Ormes 45140", "Ingré 45140", "Saint-Pryvé-Saint-Mesmin 45750", "Saint-Hilaire-Saint-Mesmin 45160",
                        "La Chapelle-Saint-Mesmin 45380", "Saint-Denis-en-Val 45560", "Saint-Cyr-en-Val 45590", "Marigny-les-Usages 45760",
                        "Pithiviers 45300", "Montargis 45200", "Gien 45500", "Beaugency 45190",
                        "Châteauneuf-sur-Loire 45110", "Sully-sur-Loire 45600", "Meung-sur-Loire 45130", "Patay 45310",
                    ].map((c) => (
                        <div key={c} className="flex items-center gap-2"><MapPin className="h-3 w-3 text-[#E63946]" />{c}</div>
                    ))}
                </div>
                <p className="text-base text-zinc-700 leading-relaxed">
                    Que vous habitiez en plein centre d'Orléans (quartiers Bourgogne, Carmes, Madeleine, Dunois, Saint-Marceau) ou dans une commune périphérique,
                    notre <strong>artisan serrurier dépanneur</strong> arrive rapidement, équipé du matériel nécessaire pour intervenir dès la première visite.
                    Notre véhicule reste géolocalisé dans le secteur orléanais 24h/24, ce qui garantit un <strong>délai d'intervention moyen de 25 à 30 minutes</strong>.
                </p>
            </Section>

            {/* --- Maillage interne — Villes du Loiret avec page dédiée --- */}
            <Section>
                <div className="text-xs uppercase tracking-[0.22em] font-semibold text-zinc-500 mb-3">◉ Serrurier dans tout le Loiret</div>
                <h2 className="font-display font-bold uppercase text-2xl sm:text-3xl tracking-tight leading-tight mb-5">
                    Serrurier dans les villes du Loiret
                </h2>
                <p className="text-base text-zinc-700 leading-relaxed mb-6">
                    Notre artisan intervient également dans toutes les grandes communes du département. Découvrez nos pages dédiées par ville :
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {CITIES.map((c) => (
                        <Link
                            key={c.slug}
                            to={`/serrurier-${c.slug}-45`}
                            className="group flex items-center justify-between gap-3 bg-zinc-50 hover:bg-[#E63946] hover:text-white border border-zinc-200 hover:border-[#E63946] p-4 transition-colors"
                            data-testid={`mesh-link-${c.slug}`}
                        >
                            <div className="flex items-center gap-3">
                                <MapPin className="h-4 w-4 text-[#E63946] group-hover:text-white" strokeWidth={2.4} />
                                <div>
                                    <div className="font-display font-bold text-sm">Serrurier {c.name}</div>
                                    <div className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 group-hover:text-white/80 font-semibold">{c.postal}</div>
                                </div>
                            </div>
                            <ArrowUpRight className="h-4 w-4 text-zinc-400 group-hover:text-white" />
                        </Link>
                    ))}
                </div>
            </Section>

            {/* --- 5. Confiance / certifications --- */}
            <Section>
                <h2 className="font-display font-bold uppercase text-2xl sm:text-3xl tracking-tight leading-tight mb-5">
                    Un artisan serrurier diplômé et certifié à Orléans
                </h2>
                <p className="text-base text-zinc-700 leading-relaxed mb-4">
                    Le métier de serrurier souffre malheureusement de pratiques peu scrupuleuses : faux artisans, surfacturation, intervention non qualifiée.
                    Atelier Beauchamp est <strong>inscrit à la Chambre de Métiers et de l'Artisanat (CMA) du Loiret</strong>, dispose d'un <strong>numéro SIRET vérifiable</strong>,
                    d'une <strong>assurance responsabilité civile professionnelle</strong> couvrant les dommages éventuels et de la <strong>garantie décennale April</strong> sur les poses de serrures et portes blindées.
                </p>
                <p className="text-base text-zinc-700 leading-relaxed mb-4">
                    Notre artisan est titulaire d'un <strong>CAP Serrurier-Métallier</strong> complété par plusieurs années d'expérience terrain. Nous suivons régulièrement des formations
                    sur les <strong>nouvelles serrures connectées</strong>, les <strong>systèmes biométriques</strong>, les <strong>serrures à carte</strong> et les <strong>portes blindées certifiées A2P</strong>.
                </p>
                <p className="text-base text-zinc-700 leading-relaxed">
                    Toutes nos interventions sont <strong>garanties</strong> : <strong>garantie pièces de 2 ans</strong> sur les serrures posées et <strong>garantie main-d'œuvre d'un an</strong>.
                    Si une serrure que nous avons installée présente un défaut dans cette période, nous revenons gratuitement pour la remplacer ou la réparer.
                </p>
            </Section>

            {/* --- 6. Avis clients --- */}
            <Section className="bg-white border-y border-black/10">
                <h2 className="font-display font-bold uppercase text-2xl sm:text-3xl tracking-tight leading-tight mb-6">
                    Avis clients · Serrurier Orléans
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    {[
                        { n: "Catherine M.", l: "Orléans 45000", c: "Intervention en 20 min un dimanche soir. Porte claquée ouverte sans aucun dégât. Très professionnel, tarif annoncé respecté. Je recommande à 100 %." },
                        { n: "Jean-Marc D.", l: "Saint-Jean-de-Braye 45800", c: "Remplacement de serrure suite à perte de clés. Conseil de qualité, choix d'une serrure A2P 3 étoiles. Pose impeccable, prix honnête." },
                        { n: "Sophie R.", l: "Olivet 45160", c: "Pose d'une porte blindée dans un appartement en rez-de-chaussée. Travail soigné, finitions parfaites, aucune dégradation du chambranle. Très satisfaite." },
                        { n: "Mehdi B.", l: "Fleury-les-Aubrais 45400", c: "Dépannage urgent suite à effraction. Mise en sécurité dans l'heure, justificatifs pour l'assurance, remplacement complet le lendemain. Au top !" },
                    ].map((r) => (
                        <div key={r.n} className="bg-zinc-50 p-5 rounded-lg border border-zinc-200">
                            <div className="flex items-center gap-1 mb-2">
                                {[1, 2, 3, 4, 5].map((s) => <Star key={s} className="h-4 w-4 fill-[#E63946] text-[#E63946]" />)}
                            </div>
                            <p className="text-sm text-zinc-700 leading-relaxed mb-3">« {r.c} »</p>
                            <div className="text-xs font-display font-bold uppercase tracking-tight">{r.n} <span className="font-normal text-zinc-500">— {r.l}</span></div>
                        </div>
                    ))}
                </div>
            </Section>

            {/* --- 7. FAQ --- */}
            <Section>
                <h2 className="font-display font-bold uppercase text-2xl sm:text-3xl tracking-tight leading-tight mb-6">
                    Questions fréquentes · Serrurier Orléans
                </h2>
                {[
                    { q: "Combien de temps pour qu'un serrurier arrive à Orléans ?", a: "Notre délai moyen est de 25 à 30 minutes à Orléans intra-muros, et de 30 à 45 minutes en périphérie du Loiret. En cas d'extrême urgence (personne enfermée, enfant ou animal à l'intérieur, fuite d'eau), nous priorisons systématiquement le déplacement." },
                    { q: "Quel est le tarif d'un serrurier à Orléans en pleine nuit ?", a: "Une intervention de nuit (entre 20h et 7h), un dimanche ou un jour férié applique une majoration légale standard. Le tarif exact vous est annoncé clairement par téléphone avant tout déplacement. Pas de surprise sur la facture." },
                    { q: "Le serrurier peut-il ouvrir une porte sans casser ?", a: "Oui, dans 95 % des cas. Nous utilisons des techniques non destructives : crochetage, méthode du carton, ouverture aux radios pour les serrures classiques, outils spécialisés pour les serrures multipoints et portes blindées. La casse est l'exception, jamais la règle." },
                    { q: "Êtes-vous agréé par les assurances ?", a: "Oui. Atelier Beauchamp est artisan inscrit à la CMA, dispose d'une assurance RC professionnelle et une décennale April et travaille régulièrement avec les principales compagnies d'assurance habitation (Maif, Macif, Matmut, AXA, Generali, Allianz, MMA, Groupama, Crédit Agricole, etc.). Nous fournissons tous les justificatifs nécessaires." },
                    { q: "Quelles marques de serrures installez-vous ?", a: "Toutes les grandes marques françaises et européennes : Fichet, Bricard, Vachette, Picard, JPM, Mottura, Kaba, Héraclès, Mul-T-Lock, Pollux, Reelax, Tesa, City, Heracles, Iseo, Dom, FTH Thirard, Dierre, Abus." },
                    { q: "Intervenez-vous le dimanche et les jours fériés ?", a: "Oui, 7 jours sur 7, 24 heures sur 24, y compris dimanches, jours fériés et nuits. Notre service de garde fonctionne en continu pour toutes les urgences serrurerie sur Orléans et le Loiret." },
                ].map((f) => (
                    <div key={f.q} className="border-b border-zinc-200 py-5">
                        <h3 className="font-display font-bold text-base mb-2">{f.q}</h3>
                        <p className="text-sm text-zinc-700 leading-relaxed">{f.a}</p>
                    </div>
                ))}
            </Section>

            {/* --- 8. Galerie photos · Atelier Beauchamp en images --- */}
            <Section className="bg-white border-y border-black/10">
                <div className="text-xs uppercase tracking-[0.22em] font-semibold text-zinc-500 mb-3">◉ Atelier Beauchamp en images</div>
                <h2 className="font-display font-bold uppercase text-2xl sm:text-3xl tracking-tight leading-tight mb-6">
                    Un accueil humain, un vrai artisan à Orléans
                </h2>
                <p className="text-base text-zinc-700 leading-relaxed mb-8 max-w-2xl">
                    Une vraie personne répond au téléphone, un véhicule équipé arrive sur place, un artisan diplômé réalise l'intervention. C'est ça, Atelier Beauchamp.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <figure className="relative bg-white border border-black/10 overflow-hidden">
                        <img src={STORE_IMG} alt="Accueil téléphonique Atelier Beauchamp Orléans" className="w-full h-[280px] sm:h-[360px] object-cover" />
                        <figcaption className="absolute bottom-0 left-0 right-0 bg-white/95 px-4 py-3 border-t border-black/10">
                            <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#E63946]">Accueil téléphonique</div>
                            <div className="text-sm font-display font-bold">Une vraie personne répond — pas de plateforme</div>
                        </figcaption>
                    </figure>
                    <figure className="relative bg-white border border-black/10 overflow-hidden">
                        <img src={VAN_IMG} alt="Camionnette Atelier Beauchamp serrurier Orléans" className="w-full h-[280px] sm:h-[360px] object-cover" />
                        <figcaption className="absolute bottom-0 left-0 right-0 bg-white/95 px-4 py-3 border-t border-black/10">
                            <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#E63946]">Véhicule identifiable</div>
                            <div className="text-sm font-display font-bold">Camionnette équipée · Pièces en stock</div>
                        </figcaption>
                    </figure>
                </div>
                <figure className="relative bg-white border border-black/10 overflow-hidden mt-5">
                    <img src={BRANDS_IMG} alt="Marques de serrures posées par Atelier Beauchamp à Orléans : Fichet, Bricard, Vachette, Picard, JPM, Mottura, Kaba, Héraclès, Mul-T-Lock" className="w-full h-[200px] sm:h-[280px] object-cover" />
                    <figcaption className="absolute bottom-0 left-0 right-0 bg-white/95 px-4 py-3 border-t border-black/10">
                        <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-[#E63946]">Toutes les grandes marques</div>
                        <div className="text-sm font-display font-bold">Fichet · Bricard · Vachette · Picard · JPM · Mottura · Kaba · Héraclès · Mul-T-Lock · Pollux · Iseo · Dom · Abus</div>
                    </figcaption>
                </figure>
            </Section>

            {/* --- 9. CTA Appel + Formulaire de contact --- */}
            <Section id="contact" className="bg-[#0A0A0A] text-white py-16">
                <h2 className="font-display font-bold uppercase text-2xl sm:text-4xl tracking-tight leading-tight mb-5 text-center">
                    Appelez votre serrurier Orléans maintenant
                </h2>
                <p className="text-base text-zinc-300 text-center max-w-2xl mx-auto mb-8">
                    Disponible immédiatement, 24h/24, 7j/7. Un artisan diplômé, un tarif clair, une intervention rapide.
                </p>
                <div className="flex justify-center">
                    <a href={TEL_HREF} className="inline-flex items-center gap-4 bg-[#E63946] hover:bg-[#D62828] text-white px-8 py-6 transition-colors text-xl sm:text-2xl font-display font-bold tracking-tight">
                        <Phone className="h-6 w-6 animate-pulse" strokeWidth={2.5} />
                        {TEL}
                        <ArrowUpRight className="h-6 w-6" />
                    </a>
                </div>
            </Section>

            <DevisForm />

            <SeoZonesFooter region="loiret" currentPath="/serrurier-orleans-45" />

            <footer className="bg-black text-zinc-500 text-center py-6 text-xs uppercase tracking-[0.18em]">
                © {new Date().getFullYear()} Atelier Beauchamp — Artisan serrurier Orléans · Tous droits réservés.
            </footer>

            {/* --- Floating call button (mobile only, clignote) --- */}
            <FloatingCallButton />
        </div>
    );
};

// --- Formulaire de devis intégré (envoie vers POST /api/contact + Resend) ---
const DevisForm = () => {
    const [form, setForm] = useState({ name: "", phone: "", email: "", city: "Orléans", service: "", message: "" });
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.phone.trim() || !form.email.trim()) {
            toast.error("Merci d'indiquer votre nom, téléphone et email.");
            return;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
            toast.error("Adresse email invalide.");
            return;
        }
        setSubmitting(true);
        try {
            await axios.post(`${API}/contact`, form);
            setDone(true);
            toast.success("Demande envoyée. Nous vous rappelons rapidement.");
        } catch {
            toast.error("Erreur. Appelez directement le 06 49 50 18 43.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section id="devis-form" className="bg-white border-t border-black/10 scroll-mt-20" data-testid="devis-form-section">
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
                <div className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#E63946] mb-3">◉ Demande de devis gratuit</div>
                <h2 className="font-display font-bold uppercase text-2xl sm:text-4xl tracking-tight leading-tight mb-3">
                    Recevez votre devis serrurier Orléans en 2 minutes
                </h2>
                <p className="text-base text-zinc-600 leading-relaxed mb-8">
                    Décrivez votre besoin, l'artisan vous rappelle rapidement avec un tarif clair. Pour toute urgence — porte claquée, effraction, serrure bloquée — privilégiez l'appel direct au <a href={TEL_HREF} className="text-[#E63946] font-semibold hover:underline">{TEL}</a>.
                </p>

                {done ? (
                    <div className="bg-green-50 border border-green-200 p-8 text-center rounded">
                        <Check className="h-12 w-12 text-green-600 mx-auto mb-3" strokeWidth={2.4} />
                        <div className="font-display font-bold uppercase text-lg mb-2">Demande envoyée ✓</div>
                        <p className="text-sm text-zinc-600">Nous vous rappelons rapidement. Pour toute urgence immédiate, appelez le {TEL}.</p>
                    </div>
                ) : (
                    <form onSubmit={submit} className="space-y-5" data-testid="devis-form">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <div className="space-y-2">
                                <label className="text-xs uppercase tracking-[0.18em] font-semibold">Nom *</label>
                                <input value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Votre nom" required
                                    className="w-full border border-black/20 px-3 py-3 focus:outline-none focus:border-black" data-testid="devis-name" />
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs uppercase tracking-[0.18em] font-semibold">Téléphone *</label>
                                <input value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="06 XX XX XX XX" required
                                    className="w-full border border-black/20 px-3 py-3 focus:outline-none focus:border-black" data-testid="devis-phone" />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <label className="text-xs uppercase tracking-[0.18em] font-semibold">Email *</label>
                            <input type="email" value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="vous@exemple.fr" required
                                className="w-full border border-black/20 px-3 py-3 focus:outline-none focus:border-black" data-testid="devis-email" />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                            <div className="space-y-2">
                                <label className="text-xs uppercase tracking-[0.18em] font-semibold">Ville</label>
                                <input value={form.city} onChange={(e) => update("city", e.target.value)} placeholder="Orléans"
                                    className="w-full border border-black/20 px-3 py-3 focus:outline-none focus:border-black" data-testid="devis-city" />
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs uppercase tracking-[0.18em] font-semibold">Service</label>
                                <select value={form.service} onChange={(e) => update("service", e.target.value)}
                                    className="w-full border border-black/20 px-3 py-3 bg-white focus:outline-none focus:border-black" data-testid="devis-service">
                                    <option value="">Sélectionner…</option>
                                    <option>Ouverture de porte claquée</option>
                                    <option>Dépannage serrurier urgent</option>
                                    <option>Remplacement de serrure</option>
                                    <option>Pose de porte blindée</option>
                                    <option>Autre demande</option>
                                </select>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <label className="text-xs uppercase tracking-[0.18em] font-semibold">Message</label>
                            <textarea value={form.message} onChange={(e) => update("message", e.target.value)} rows={4}
                                placeholder="Décrivez votre besoin (adresse, urgence, type de porte/serrure…)"
                                className="w-full border border-black/20 px-3 py-3 focus:outline-none focus:border-black" data-testid="devis-message" />
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3 pt-2">
                            <button type="submit" disabled={submitting}
                                className="inline-flex items-center justify-center gap-3 bg-[#0A0A0A] hover:bg-[#E63946] text-white px-6 py-4 transition-colors disabled:opacity-50"
                                data-testid="devis-submit">
                                {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <Mail className="h-5 w-5" />}
                                <span className="font-semibold uppercase tracking-[0.15em] text-sm">{submitting ? "Envoi…" : "Envoyer ma demande"}</span>
                            </button>
                            <a href={TEL_HREF} className="inline-flex items-center justify-center gap-3 border-2 border-[#E63946] text-[#E63946] hover:bg-[#E63946] hover:text-white px-6 py-4 transition-colors">
                                <Phone className="h-5 w-5" />
                                <span className="font-semibold uppercase tracking-[0.15em] text-sm">Ou appeler {TEL}</span>
                            </a>
                        </div>
                        <p className="text-[10px] text-zinc-500 uppercase tracking-wide">Vos données sont uniquement utilisées pour vous recontacter — aucune transmission à des tiers.</p>
                    </form>
                )}
            </div>
        </section>
    );
};

export default SerrurierOrleans;
